import { Combobox as ComboboxPrimitive, type ComboboxItemState } from '@base-ui/react';
import { CheckIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { mergeStyling } from '../../common-functions';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../input-group/input-group';

const Combobox = ComboboxPrimitive.Root;

const ComboboxValue = ({ ...props }: ComboboxPrimitive.Value.Props) => {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
};

const ComboboxTrigger = ({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) => {
  const finalClassName = mergeStyling(
    'odin-shrink odin-ml-1 odin-px-1 odin-border-l odin-border-gray-4 focus:odin-outline-none',
    className
  );
  return (
    <ComboboxPrimitive.Trigger data-slot="combobox-trigger" className={finalClassName} {...props}>
      {children}

      <OdinIcon
        size={OdinIconSize.Small}
        icon="ChevronDown"
        className="[[data-slot=input-group]:has([data-popup-open])_&]:odin-hidden"
      />

      <OdinIcon
        size={OdinIconSize.Small}
        icon="ChevronUp"
        className="odin-hidden [[data-slot=input-group]:has([data-popup-open])_&]:odin-block"
      />
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxClear = ({ className, ...props }: ComboboxPrimitive.Clear.Props) => {
  const finalClassName = mergeStyling(
    'odin-shrink odin-ml-1 odin-cursor-pointer odin-px-2 odin-text-gray-3',
    className
  );
  return (
    <ComboboxPrimitive.Clear data-slot="combobox-clear" className={finalClassName} {...props}>
      <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />
    </ComboboxPrimitive.Clear>
  );
};

const ComboboxInput = ({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  error = false,
  inputClassName,
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean;
  showClear?: boolean;
  error?: boolean;
  inputClassName?: string;
}) => {
  const finalStyles = mergeStyling(
    'odin-relative odin-py-1 odin-outline-none odin-flex-1 odin-items-center odin-justify-center odin-w-full focus:odin-border-primary-1 focus:odin-ring-1 focus:odin-ring-primary-1',
    className,
    { 'odin-border odin-border-error': error }
  );

  return (
    <InputGroup className={finalStyles}>
      <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} className={inputClassName} />} {...props} />
      <InputGroupAddon
        align={null}
        className="odin-box-border odin-absolute odin-flex odin-items-center odin-justify-center odin-h-full odin-right-0 odin-rounded odin-border-none "
      >
        {showClear && <ComboboxClear />}
        {showTrigger && <ComboboxTrigger />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
};

const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 3,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) => {
  const popupStyles = mergeStyling(
    'odin-bg-mono-1 odin-text-gray-1 odin-ring-gray-4 odin-overflow-hidden',
    'odin-shadow-md odin-ring-1 *:data-[slot=input-group]:odin-m-1 *:data-[slot=input-group]:odin-mb-0 *:data-[slot=input-group]:odin-h-8 *:data-[slot=input-group]:odin-shadow-none group/combobox-content odin-max-h-[var(--available-height)]',
    'odin-w-[var(--anchor-width)] odin-origin-[var(--transform-origin)] data-[chips=true]:odin-min-w-[var(--anchor-width)]',
    className
  );
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="odin-isolate odin-z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={popupStyles}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

const ComboboxList = ({ className, ...props }: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={mergeStyling(
        'no-scrollbar odin-max-h-[min(18rem,calc(var(--available-height)-2.25rem))] odin-scroll-py-1 odin-p-1 data-empty:odin-p-0 odin-overflow-y-auto odin-overscroll-contain',
        className
      )}
      {...props}
    />
  );
};

const ComboboxItem = ({
  className,
  children,
  showIndicator = true,
  ...props
}: ComboboxPrimitive.Item.Props & { showIndicator?: boolean }) => {
  const appliedStyles = (state: ComboboxItemState) => (typeof className === 'function' ? className(state) : className);
  const finalClassName = (state: ComboboxItemState) =>
    mergeStyling(
      'data-[selected]:odin-bg-gray-2 data-[selected]:odin-text-mono-1',
      "data-[highlighted]:odin-bg-primary-5 data-[highlighted]:odin-text-primary-2 not-data-[variant=destructive]:data-[highlighted]:**:odin-text-primary-2 odin-gap-2 odin-py-1 odin-pr-8 odin-pl-1.5 odin-text-sm [&_svg:not([class*='odin-size-'])]:odin-size-4 odin-relative odin-flex odin-w-full odin-cursor-default odin-items-center odin-outline-none odin-select-none data-[disabled]:odin-pointer-events-none data-[disabled]:odin-opacity-50 [&_svg]:odin-pointer-events-none [&_svg]:odin-shrink-0",
      appliedStyles(state)
    );

  return (
    <ComboboxPrimitive.Item data-slot="combobox-item" className={finalClassName} {...props}>
      {children}
      {showIndicator && (
        <ComboboxPrimitive.ItemIndicator
          render={
            <span className="odin-pointer-events-none odin-absolute odin-right-2 odin-flex odin-size-4 odin-items-center odin-justify-center">
              <CheckIcon className="odin-pointer-events-none" />
            </span>
          }
        />
      )}
    </ComboboxPrimitive.Item>
  );
};

const ComboboxGroup = ({ className, ...props }: ComboboxPrimitive.Group.Props) => {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={mergeStyling(className)} {...props} />;
};

const ComboboxLabel = ({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) => {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={mergeStyling('odin-text-gray-2 odin-px-2 odin-py-1.5 odin-text-xs', className)}
      {...props}
    />
  );
};

const ComboboxCollection = ({ ...props }: ComboboxPrimitive.Collection.Props) => {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
};

const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={mergeStyling(
        'odin-text-gray-2 odin-w-full odin-justify-center odin-pt-2 odin-text-center odin-text-sm group-data-empty/combobox-content:odin-flex',
        className
      )}
      {...props}
    />
  );
};

const ComboboxSeparator = ({ className, ...props }: ComboboxPrimitive.Separator.Props) => {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={mergeStyling('odin-bg-gray-4 odin--mx-1 odin-my-1 odin-h-px', className)}
      {...props}
    />
  );
};

const ComboboxChips = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) => {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={mergeStyling(
        'odin-border-gray-4 focus-within:odin-border-primary-2 has-aria-invalid:odin-border-error odin-flex odin-min-h-8 odin-flex-wrap odin-items-center odin-gap-1 odin-border odin-bg-transparent odin-bg-clip-padding odin-px-2.5 odin-py-1 odin-text-sm focus-within:odin-ring-3 has-aria-invalid:odin-ring-3 has-data-[slot=combobox-chip]:odin-px-1',
        className
      )}
      {...props}
    />
  );
};

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) => {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={mergeStyling(
        'odin-bg-gray-5 odin-text-gray-1 odin-flex odin-h-[1.3125rem] odin-w-fit odin-items-center odin-justify-center odin-gap-1 odin-px-1.5 odin-text-xs odin-font-medium odin-whitespace-nowrap has-data-[slot=combobox-chip-remove]:odin-pr-0 has-disabled:odin-pointer-events-none has-disabled:odin-cursor-not-allowed has-disabled:odin-opacity-50',
        className
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          className="odin--ml-1 odin-opacity-50 hover:odin-opacity-100"
          data-slot="combobox-chip-remove"
        >
          <XIcon className="odin-cursor-pointer odin-text-gray-2 odin-size-4" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
};

const ComboboxChipsInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={mergeStyling('odin-min-w-16 odin-flex-1 odin-outline-none', className)}
      {...props}
    />
  );
};

const useComboboxAnchor = () => {
  return React.useRef<HTMLDivElement | null>(null);
};

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
};
